import { Api, Category } from '@square/ignition';
import {
  ReferenceHrefBuilders,
  ReferencePath,
} from '@squareup/dex-tech-utils-routing';
import { NullableClassName } from '@squareup/dex-types-shared-ui';
import { NavigationItem, NavigationList } from '@squareup/dex-ui';
import { Box } from '@squareup/dex-ui-shared-base';
import { NavigationMobileToggleProps } from '@squareup/dex-ui-shared-mobile-nav';
import React from 'react';

import { ReferenceStickyNavigation } from '../ReferenceStickyNavigation';

import styles from './reference-navigation.module.css';

interface ReferenceNavigationProps {
  activeItem?: Category | Api | undefined;
  activeVersion: string;
  categories: Category[] | undefined;
  pathname: string;
}

const SubNavigation = ({
  apis,
  activeItem,
  activeVersion,
}: {
  apis: Api[];
  activeItem: Api | undefined;
  activeVersion: string;
}) => {
  return (
    <NavigationList isSubNavigation={true}>
      {apis.map(({ name, id }) => {
        const api = `${id}-api`;
        return (
          <NavigationItem
            trackingId={`navigate-to-reference-api-${id}`}
            isSubItem={true}
            key={id}
            href={ReferenceHrefBuilders.versionApi({
              api,
              square_version: activeVersion,
            })}
            testId={id}
            text={name}
            isActive={activeItem?.id === id}
          ></NavigationItem>
        );
      })}
    </NavigationList>
  );
};

const itemPathNames = {
  objects: [ReferencePath.VERSION_ENUMS, ReferencePath.VERSION_OBJECTS],
  webhooks: [ReferencePath.VERSION_WEBHOOKS],
  deprecated: [ReferencePath.VERSION_DEPRECATED],
  sdks: [] as ReferencePath[],
};

const ReferenceNavigation = React.memo(
  ({
    className,
    activeVersion,
    activeItem,
    categories,
    pathname,
    ...rest
  }: ReferenceNavigationProps &
    NullableClassName &
    NavigationMobileToggleProps) => {
    if (!activeVersion) {
      return null;
    }

    const homeItem = {
      href: ReferenceHrefBuilders.version({ square_version: activeVersion }),
      id: 'home',
      testId: 'Home',
      text: 'Home',
    };

    const additionalItems = [
      {
        href: ReferenceHrefBuilders.versionObjects({
          square_version: activeVersion,
        }),
        id: 'objects',
        testId: 'Objects',
        text: 'Objects & Enums',
        pathNames: itemPathNames['objects'],
      },
      {
        href: ReferenceHrefBuilders.versionWebhooks({
          square_version: activeVersion,
        }),
        id: 'webhooks',
        testId: 'Webhooks',
        text: 'Webhook Events',
        pathNames: itemPathNames['webhooks'],
      },
      {
        href: ReferenceHrefBuilders.versionDeprecated({
          square_version: activeVersion,
        }),
        id: 'deprecated',
        testId: 'Deprecated',
        text: 'Deprecated',
        pathNames: itemPathNames['deprecated'],
      },
      {
        href: '/docs/sdks',
        id: 'sdks',
        testId: 'SDKs',
        text: 'SDKs',
        pathNames: itemPathNames['sdks'],
      },
    ];

    return (
      <ReferenceStickyNavigation
        testId="reference-navigation"
        className={className}
        {...rest}
      >
        <NavigationList enableOverflowShimmer={true}>
          {
            <NavigationItem
              trackingId={`navigate-to-reference-${homeItem.id}`}
              isActive={pathname === ReferencePath.VERSION}
              key={homeItem.id}
              {...homeItem}
            ></NavigationItem>
          }
          {categories?.map((item) => {
            const { name, id, apis } = item;
            let prefix = id;
            // if apis exist, use fist element as default for href
            if (apis.length > 0 && apis[0]) {
              prefix = apis[0].id;
            }
            const api = `${prefix}-api`;

            let isActive = false;
            let isExpanded = false;

            /**
             * The graph entities have no type field for consumers for some reason. The incoming active item could be one of any type.
             * So for now just check to see if it has a category prop to tell if its a category or not.
             **/
            const isActiveItemACategory =
              activeItem && 'category' in activeItem;

            if (activeItem) {
              if (isActiveItemACategory) {
                isActive = id === activeItem.category?.id;

                // If there is only one api no point in expanding, the api is the same as the category.
                isExpanded = Boolean(
                  isActive &&
                    activeItem.category &&
                    activeItem.category.apis?.length > 1
                );
              } else {
                isActive = id === activeItem?.id;
              }
            }

            return (
              <NavigationItem
                trackingId={`navigate-to-reference-${id}`}
                key={id}
                href={ReferenceHrefBuilders.versionApi({
                  api,
                  square_version: activeVersion,
                })}
                isActive={isActive}
                testId={id}
                text={name}
              >
                {isActiveItemACategory && isExpanded ? (
                  <SubNavigation
                    activeVersion={activeVersion}
                    apis={apis.filter(
                      // Ensure we remove deprecated or empty apis
                      (api) => !api.isDeprecated && api.endpoints.length > 0
                    )}
                    activeItem={activeItem}
                  />
                ) : null}
              </NavigationItem>
            );
          })}
          <li>
            <Box
              className={styles.divider}
              margin={{ top: '3x', right: '5x', bottom: '2x', left: '2x' }}
              padding={{ top: '1x' }}
            />
          </li>
          {additionalItems.map((item) => {
            return (
              <NavigationItem
                trackingId={`navigate-to-reference-${item.id}`}
                isActive={item.pathNames.includes(pathname as ReferencePath)}
                isSubItem={true}
                key={item.id}
                {...item}
              ></NavigationItem>
            );
          })}
        </NavigationList>
      </ReferenceStickyNavigation>
    );
  }
);

ReferenceNavigation.displayName = 'ReferenceNavigation';

export { ReferenceNavigation };
